import { useEffect, useRef, useState } from "react";
import { IconMic, IconMicOff } from "@/components/ui/icons";
import { Modal } from "@/components/ui/Modal";
import { api } from "@/lib/api";
import { usePlayer } from "@/hooks/usePlayer";
import { usePlayerProgress } from "@/hooks/usePlayerProgress";

type Line = { t: number; text: string };
type LyricsResp = {
  found: boolean;
  synced: boolean;
  lines: Line[];
  plain: string;
  instrumental?: boolean;
};

export function KaraokeView() {
  const [open, setOpen] = useState(false);
  const { current } = usePlayer();
  const progress = usePlayerProgress();
  const [data, setData] = useState<LyricsResp | null>(null);
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);

  // fetch on every track change (not gated by `open`) so the mic button
  // itself can show "no lyrics" without requiring the user to open the
  // karaoke view first
  useEffect(() => {
    if (!current?.id) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setData(null);
    api<LyricsResp>(`/api/lyrics/${current.id}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ found: false, synced: false, lines: [], plain: "" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [current?.id]);

  const noLyrics = !!data && (!data.found || !!data.instrumental);

  // find active line index (last line whose t <= progress)
  const lines = data?.lines ?? [];
  let activeIdx = -1;
  if (data?.synced && lines.length) {
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].t <= progress) activeIdx = i;
      else break;
    }
  }

  // auto-center the active line
  useEffect(() => {
    if (activeIdx < 0 || !activeRef.current || !scrollerRef.current) return;
    const el = activeRef.current;
    const scroller = scrollerRef.current;
    const target = el.offsetTop - scroller.clientHeight / 2 + el.clientHeight / 2;
    scroller.scrollTo({ top: target, behavior: "smooth" });
  }, [activeIdx]);

  return (
    <>
      <button title={noLyrics ? "Pas de paroles pour ce morceau" : "Paroles"} onClick={() => setOpen(true)}>
        {noLyrics ? <IconMicOff /> : <IconMic />}
      </button>
      <Modal open={open} onClose={() => setOpen(false)}
             title={current ? `${current.title} — ${current.artist?.name ?? ""}` : "Paroles"}>
        <div
          ref={scrollerRef}
          style={{
            maxHeight: "60vh", overflowY: "auto",
            padding: "24px 8px", scrollBehavior: "smooth",
            maskImage: "linear-gradient(180deg, transparent 0, #000 12%, #000 88%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(180deg, transparent 0, #000 12%, #000 88%, transparent 100%)",
          }}
        >
          {loading && <div className="muted" style={{ textAlign: "center" }}>Chargement des paroles…</div>}
          {!loading && data?.instrumental && (
            <div className="muted" style={{ textAlign: "center", fontStyle: "italic" }}>
              🎶 morceau instrumental
            </div>
          )}
          {!loading && data && !data.found && (
            <div className="muted" style={{ textAlign: "center" }}>
              Pas de paroles trouvées pour ce morceau sur LRClib.
            </div>
          )}
          {!loading && data?.synced && lines.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {lines.map((l, i) => {
                const isActive = i === activeIdx;
                const isPast = i < activeIdx;
                return (
                  <div
                    key={i}
                    ref={isActive ? activeRef : undefined}
                    style={{
                      textAlign: "center",
                      fontSize: isActive ? 24 : 18,
                      fontWeight: isActive ? 800 : 500,
                      color: isActive ? "var(--fg)" : isPast ? "var(--fg-mute)" : "rgba(255,255,255,.5)",
                      opacity: isActive ? 1 : isPast ? 0.6 : 0.8,
                      lineHeight: 1.3,
                      transform: isActive ? "scale(1.02)" : "scale(1)",
                      transition: "font-size .25s var(--ease-out), color .25s var(--ease-out), transform .25s var(--ease-out), opacity .25s var(--ease-out)",
                      textShadow: isActive ? "0 0 24px rgba(167,139,255,.5)" : "none",
                    }}
                  >
                    {l.text || "♪"}
                  </div>
                );
              })}
            </div>
          )}
          {!loading && data && !data.synced && data.plain && (
            <pre style={{
              whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 15,
              lineHeight: 1.6, color: "var(--fg-mute)", textAlign: "center",
            }}>{data.plain}</pre>
          )}
        </div>
      </Modal>
    </>
  );
}
